import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "../../firebase";
import { useAuthState } from "react-firebase-hooks/auth";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import { FaArrowLeft } from "react-icons/fa";
import DatePickerWithValidation from "../ui/DatePickerWithValidation";

const Profile = () => {
  const navigate = useNavigate();
  const [user] = useAuthState(auth);
  const { profile: contextProfile } = useAuth(); // Get profile from AuthContext (fetched via API)
  const [profile, setProfile] = useState({
    firstName: "",
    lastName: "",
    gender: "",
    dob: "",
    nationality: "",
    city: "",
    state: "",
    mobile: "",
    email: "",
    passportNo: "",
    passportExpiry: "",
    issuingCountry: "",
    photoURL: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Load profile from AuthContext (fetched via secure API, not direct Firestore)
  useEffect(() => {
    if (!user || !contextProfile) {
      if (user && !contextProfile) {
        // User exists but profile not loaded yet
        setLoading(true);
      } else {
        setLoading(false);
      }
      return;
    }
    
    setLoading(true);
    // Use profile from AuthContext (fetched via secure API)
    setProfile((prev) => ({
      ...prev,
      firstName: contextProfile.firstName || "",
      lastName: contextProfile.lastName || "",
      gender: contextProfile.gender || "",
      dob: contextProfile.dob || "",
      nationality: contextProfile.nationality || "",
      city: contextProfile.city || "",
      state: contextProfile.state || "",
      mobile: contextProfile.mobile || "",
      email: contextProfile.email || user.email || "",
      passportNo: contextProfile.passportNo || "",
      passportExpiry: contextProfile.passportExpiry || "",
      issuingCountry: contextProfile.issuingCountry || "",
      photoURL: contextProfile.photoURL || "",
    }));
    setLoading(false);
  }, [user, contextProfile]);

  // --- Profile field change handler ---
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setProfile((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // --- Save profile via secure API ---
  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    try {
      await axios.put(
        `/profile/update?uid=${user.uid}`,
        { ...profile },
        { withCredentials: true }
      );
      alert("Profile saved successfully!");
      // Optionally refresh profile by reloading page or refetching
      window.location.reload();
    } catch (err: any) {
      alert("Failed to save profile: " + (err.response?.data?.error || err.message));
    }
    setSaving(false);
  };

  if (loading) return <div>Loading profile...</div>;

  return (
    <div className="max-w-screen-xl mx-auto bg-white rounded-xl shadow-md p-8 my-8">
      <button
        className="text-sm font-semibold text-primary hover:text-heading transition flex items-center mb-4"
        onClick={() => navigate(-1)}
        type="button"
      >
        <FaArrowLeft className="w-4 h-4 mr-2" />
        Back
      </button>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-3xl font-sora font-bold">My Profile</h1>
        <button
          className="bg-primary text-white text-sm font-semibold px-4 py-1.5 rounded hover:bg-gray-100 hover:text-primary transition flex items-center justify-center min-w-[110px]"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving..." : "Save"}
        </button>
      </div>

      <h2 className="text-base text-heading font-semibold mb-6">
        General Information
      </h2>
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-xs font-semibold text-primary mb-1">
            First & Middle Name
          </label>
          <input
            className="w-full border rounded-xl px-3 py-2"
            name="firstName"
            value={profile.firstName}
            onChange={handleChange}
            placeholder="Enter first & middle name"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-primary mb-1">
            Last Name
          </label>
          <input
            className="w-full border rounded-xl px-3 py-2"
            name="lastName"
            value={profile.lastName}
            onChange={handleChange}
            placeholder="Enter last name"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-primary mb-1">
            Gender
          </label>
          <select
            className="w-full border rounded-xl px-3 py-2"
            name="gender"
            value={profile.gender}
            onChange={handleChange}
          >
            <option value="">Select</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-primary mb-1">
            Date of Birth
          </label>
          <DatePickerWithValidation
            selected={profile.dob}
            onSelect={(date) => {
              setProfile((prev) => ({ ...prev, dob: date }));
            }}
            placeholder="Select date of birth"
            maxDate={new Date().toISOString().split("T")[0]}
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-primary mb-1">
            Nationality
          </label>
          <select
            className="w-full border rounded-xl px-3 py-2"
            name="nationality"
            value={profile.nationality}
            onChange={handleChange}
          >
            <option value="">Select</option>
            <option value="IN">India</option>
            <option value="AE">UAE</option>
            {/* Add more as needed */}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-primary mb-1">
            City of Residence
          </label>
          <input
            className="w-full border rounded-xl px-3 py-2"
            name="city"
            value={profile.city}
            onChange={handleChange}
            placeholder="Enter city"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-primary mb-1">
            State
          </label>
          <input
            className="w-full border rounded-xl px-3 py-2"
            name="state"
            value={profile.state}
            onChange={handleChange}
            placeholder="Enter state"
          />
        </div>
      </div>

      <h2 className="text-base text-heading mb-4 font-semibold">
        Contact Details
      </h2>
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-xs font-semibold text-primary mb-1">
            Mobile Number
          </label>
          <input
            className="w-full border rounded-xl px-3 py-2"
            name="mobile"
            value={profile.mobile}
            onChange={handleChange}
            placeholder="Enter mobile number"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-primary mb-1">
            Email ID
          </label>
          <input
            className="w-full border rounded-xl px-3 py-2"
            name="email"
            value={profile.email}
            onChange={handleChange}
            placeholder="Enter email ID"
          />
        </div>
      </div>

      <h2 className="text-base text-heading font-semibold mb-4">
        Documents Details
      </h2>
      <div className="grid grid-cols-2 gap-4 mb-2">
        <div>
          <label className="block text-xs font-semibold text-primary mb-1">
            Passport Number
          </label>
          <input
            className="w-full border rounded-xl px-3 py-2"
            name="passportNo"
            value={profile.passportNo}
            onChange={handleChange}
            placeholder="Enter passport number"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-primary mb-1">
            Expiry Date
          </label>
          <DatePickerWithValidation
            selected={profile.passportExpiry}
            onSelect={(date) => {
              setProfile((prev) => ({ ...prev, passportExpiry: date }));
            }}
            placeholder="Select expiry date"
            minDate={new Date().toISOString().split("T")[0]}
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-primary mb-1">
            Issuing Country
          </label>
          <select
            className="w-full border rounded-xl px-3 py-2"
            name="issuingCountry"
            value={profile.issuingCountry}
            onChange={handleChange}
          >
            <option value="">Select country</option>
            <option value="IN">India</option>
            <option value="AE">UAE</option>
            {/* Add more countries as needed */}
          </select>
        </div>
      </div>

      {/* ...existing profile fields and save button... */}
      {/* Place this at the end to keep the save button for profile info */}
      <div className="flex items-center justify-between mt-8">
        <div />
        <button
          className="bg-primary text-white text-sm font-semibold px-4 py-1.5 rounded hover:bg-gray-100 hover:text-primary transition flex items-center justify-center min-w-[110px]"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
};

export default Profile;
